import { Stack } from "@fluentui/react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

import { BuildContractColumns } from "./components/buildContractColumns/BuildContractColumns";
import { CONTRACTS_QUERY_TOKEN } from "./constants";
import { IconNames, TranslatedContractNextStep } from "./enums";
import { IUseGetContractsResponse, PendingContractOptions } from "./interfaces";
import { ContractProps } from "./types";
import { useContractFilters } from "./useContractFilters";

import { ViewContract } from "@/components/Contracts/ViewContract";
import { GoTo } from "@/components/goTo";
import { Card } from "@/components/Shared/Card";
import {
  CustomDrawer,
  DrawerActions,
  DrawerModules
} from "@/components/Shared/CustomDrawer";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import { Table } from "@/core/libs/table";
import { useSelectedBu } from "@/hooks/useSelectedBu";
import { useURLFilter } from "@/hooks/useUrlFilter";

interface DrawerState {
  isOpen: boolean;
  action: DrawerActions;
}

function Contracts({ canRead, canCreate }: ContractProps): JSX.Element {
  const navigate = useNavigate();
  const { selectedBU, selectedRegion, selectedSegment } = useSelectedBu();
  const { urlParams } = useURLFilter();
  const { filters, canListShipments } = useContractFilters();

  const canCreateContract =
    canCreate && selectedBU ? () => navigate("/contracts/create") : undefined;

  const [drawerState, setDrawerState] = useState<DrawerState>({
    action: null,
    isOpen: false
  });
  const [selectedItemId, setSelectedItemId] = useState<number>(undefined);

  const [pendingContractOptions, setPendingContractOptions] = useState<
    Array<PendingContractOptions>
  >([]);

  const paramValues = {
    businessActionId: selectedBU?.id,
    businessUnitSelected: selectedBU?.id,
    all: true,
    region: selectedRegion,
    segment: selectedSegment,
    ...urlParams.params
  };

  const { data: contractsList, isFetching: isLoadingContractsList } =
    useGet<IUseGetContractsResponse>({
      url: "contracts",
      queryToken: CONTRACTS_QUERY_TOKEN,
      params: paramValues
    });

  function openDrawer(action: DrawerActions) {
    setDrawerState({ action, isOpen: true });
  }

  function closeDrawer() {
    setDrawerState({ action: null, isOpen: false });
  }

  useEffect(() => {
    if (contractsList?.pendingContracts) {
      const formattedPendingContractOptions =
        contractsList.pendingContracts.map(contract => ({
          text: TranslatedContractNextStep[contract.internalName],
          iconName: IconNames[contract.internalName],
          queryValue: contract.internalName,
          count: contract.qty
        }));

      setPendingContractOptions(formattedPendingContractOptions);
    }
  }, [contractsList]);

  return (
    <Stack styles={{ root: { width: "100%" } }} tokens={{ childrenGap: 15 }}>
      <Helmet>
        <title>Contratos - Libertas</title>
      </Helmet>

      <Card padding="1rem 0">
        <Stack
          horizontal
          horizontalAlign="space-between"
          style={{ padding: "1rem" }}
        >
          <h2 className="ms-fontSize-24">Contratos</h2>
          <GoTo route="contracts" label="Ir para contrato" />
        </Stack>

        <Table
          headerAction={canCreateContract}
          columns={BuildContractColumns({
            listItems: contractsList?.contracts?.items,
            canRead,
            canListShipments,
            openDrawer,
            setSelectedItemId
          })}
          list={contractsList?.contracts}
          isLoadingList={isLoadingContractsList}
          filters={filters}
          primaryButtonText={"Novo Contrato"}
          headerFilters={{
            filterQuery: "pendingStatus",
            filtersConfig: pendingContractOptions
          }}
        />
      </Card>

      <CustomDrawer
        isOpen={drawerState.isOpen}
        action={drawerState.action}
        module={DrawerModules.CONTRACT}
        onCloseDrawer={closeDrawer}
      >
        {drawerState.action === DrawerActions.VIEW && canRead && (
          <ViewContract contractId={selectedItemId} />
        )}
      </CustomDrawer>
    </Stack>
  );
}

export default Contracts;
